import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icono, type NombreIcono } from './Icono';

/**
 * El buscador de la barra superior: llega a una página, a un proceso o a una
 * traza escribiendo, sin recorrer el menú.
 *
 * Busca solo en lo que el navegador ya sabe —las páginas del menú y los
 * nombres de proceso—, y reconoce un identificador de traza por su forma: 32
 * hexadecimales. No consulta nada por cada tecla: una búsqueda que sale a la
 * red a cada letra es lo que hace que un panel se sienta lento justo cuando
 * alguien tiene prisa.
 */

export interface Pagina {
  id: string;
  etiqueta: string;
  icono: NombreIcono;
}

interface Opcion {
  clave: string;
  etiqueta: string;
  apoyo: string;
  icono: NombreIcono;
  elegir: () => void;
}

const ES_TRAZA = /^[0-9a-f]{32}$/i;

export function BuscadorGlobal({
  paginas,
  procesos,
  puedeVerTrazas,
  alCargarProcesos,
  onPagina,
  onProceso,
  onTraza,
}: {
  paginas: Pagina[];
  /** Los nombres de proceso conocidos; vacío hasta que se pidan. */
  procesos: string[];
  puedeVerTrazas: boolean;
  /** Se llama al enfocar, para traer los nombres solo cuando hacen falta. */
  alCargarProcesos: () => void;
  onPagina: (id: string) => void;
  onProceso: (nombre: string) => void;
  onTraza: (traceId: string) => void;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activa, setActiva] = useState(0);
  const contenedor = useRef<HTMLDivElement>(null);
  const idLista = useId();

  const opciones = useMemo<Opcion[]>(() => {
    const q = texto.trim().toLowerCase();
    if (!q) return [];
    const salida: Opcion[] = [];
    if (puedeVerTrazas && ES_TRAZA.test(q)) {
      salida.push({
        clave: `traza:${q}`,
        etiqueta: `Abrir la traza ${q.slice(0, 12)}…`,
        apoyo: 'Traza',
        icono: 'traza',
        elegir: () => onTraza(q),
      });
    }
    for (const p of procesos) {
      if (p.toLowerCase().includes(q)) {
        salida.push({ clave: `proceso:${p}`, etiqueta: p, apoyo: 'Proceso', icono: 'procesos', elegir: () => onProceso(p) });
      }
    }
    for (const g of paginas) {
      if (g.etiqueta.toLowerCase().includes(q)) {
        salida.push({ clave: `pagina:${g.id}`, etiqueta: g.etiqueta, apoyo: 'Página', icono: g.icono, elegir: () => onPagina(g.id) });
      }
    }
    return salida.slice(0, 8);
  }, [texto, procesos, paginas, puedeVerTrazas, onPagina, onProceso, onTraza]);

  useEffect(() => setActiva(0), [texto]);

  useEffect(() => {
    if (!abierto) return;
    const alHacerClicFuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alHacerClicFuera);
    return () => document.removeEventListener('mousedown', alHacerClicFuera);
  }, [abierto]);

  function elegir(o: Opcion | undefined) {
    if (!o) return;
    o.elegir();
    setTexto('');
    setAbierto(false);
  }

  function alTeclear(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setActiva((i) => Math.min(opciones.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiva((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      elegir(opciones[activa]);
    } else if (e.key === 'Escape') {
      setAbierto(false);
      (e.target as HTMLInputElement).blur();
    }
  }

  const hayLista = abierto && texto.trim().length > 0;

  return (
    <div className="buscador" ref={contenedor}>
      <Icono nombre="buscar" tamano={16} />
      <input
        type="search"
        value={texto}
        placeholder="Buscar procesos, páginas o una traza…"
        aria-label="Buscar procesos, páginas o una traza"
        role="combobox"
        aria-expanded={hayLista}
        aria-controls={idLista}
        aria-activedescendant={hayLista && opciones[activa] ? `${idLista}-${activa}` : undefined}
        autoComplete="off"
        onFocus={() => {
          setAbierto(true);
          alCargarProcesos();
        }}
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
        }}
        onKeyDown={alTeclear}
      />
      {hayLista && (
        <ul className="buscador-lista" id={idLista} role="listbox">
          {opciones.length === 0 ? (
            <li className="buscador-vacio" role="option" aria-selected="false">
              Nada coincide con «{texto.trim()}».
            </li>
          ) : (
            opciones.map((o, i) => (
              <li
                key={o.clave}
                id={`${idLista}-${i}`}
                role="option"
                aria-selected={i === activa}
                className={i === activa ? 'activa' : ''}
                onMouseEnter={() => setActiva(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  elegir(o);
                }}
              >
                <Icono nombre={o.icono} tamano={16} />
                <span>{o.etiqueta}</span>
                <small>{o.apoyo}</small>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
